(() => {
  if (window.__CORGI_CART_SHIPPING_SAVER_LOADED__) return;
  window.__CORGI_CART_SHIPPING_SAVER_LOADED__ = true;

  const state = {
    activeRoot: null,
    activeTrigger: null,
    bypassCheckout: false,
    reopenAfterCartUpdate: false,
    closeTimer: null,
  };

  function toNumber(value) {
    const number = Number.parseInt(String(value || ''), 10);
    return Number.isFinite(number) ? number : 0;
  }

  function getItems(root) {
    return Array.from(root.querySelectorAll('[data-corgi-shipping-saver-item]')).filter(
      (item) => item instanceof HTMLElement
    );
  }

  function orderItems(root) {
    if (!(root instanceof HTMLElement) || root.dataset.reached === 'true') return [];

    const rail = root.querySelector('[data-corgi-shipping-saver-rail]');
    if (!(rail instanceof HTMLElement)) return [];

    const remaining = Math.max(0, toNumber(root.dataset.remaining));
    const limit = Math.max(1, toNumber(root.dataset.limit) || 13);
    const topCount = Math.max(0, toNumber(root.dataset.topCount) || 3);
    const items = getItems(root);

    const indexed = items.map((item, index) => ({
      item,
      index: toNumber(item.dataset.index) || index,
      price: Math.max(0, toNumber(item.dataset.price)),
    }));

    const overOrEqual = indexed
      .filter((entry) => entry.price >= remaining)
      .sort((a, b) => a.price - remaining - (b.price - remaining) || a.index - b.index);

    const under = indexed
      .filter((entry) => entry.price < remaining)
      .sort((a, b) => remaining - a.price - (remaining - b.price) || a.index - b.index);

    const featured = [...overOrEqual, ...under].slice(0, topCount);
    const featuredItems = new Set(featured.map((entry) => entry.item));
    const collectionOrder = indexed
      .filter((entry) => !featuredItems.has(entry.item))
      .sort((a, b) => a.index - b.index);
    const ordered = [...featured, ...collectionOrder];
    const visible = ordered.slice(0, limit).map((entry) => entry.item);
    const visibleItems = new Set(visible);

    ordered.forEach((entry) => rail.append(entry.item));
    items.forEach((item) => {
      item.hidden = !visibleItems.has(item);
    });

    root.dataset.visibleCount = String(visible.length);
    return visible;
  }

  function shouldBlockCheckout(root) {
    if (!(root instanceof HTMLElement)) return false;
    if (root.dataset.reached === 'true') return false;
    if (toNumber(root.dataset.remaining) <= 0) return false;

    return orderItems(root).length > 0;
  }

  function setLock(isLocked) {
    document.documentElement.classList.toggle('corgi-shipping-saver-lock', isLocked);
  }

  function openRoot(root, trigger = null) {
    if (!(root instanceof HTMLElement)) return;

    window.clearTimeout(state.closeTimer);
    orderItems(root);

    state.activeRoot = root;
    state.activeTrigger = trigger instanceof HTMLElement ? trigger : null;
    root.hidden = false;

    window.requestAnimationFrame(() => {
      root.classList.add('is-open');
      setLock(true);
      const focusTarget =
        root.querySelector('[data-corgi-shipping-saver-add]:not([disabled])') ||
        root.querySelector('[data-corgi-shipping-saver-continue]') ||
        root.querySelector('.corgi-shipping-saver__dialog');
      if (focusTarget instanceof HTMLElement) focusTarget.focus({ preventScroll: true });
    });
  }

  function closeRoot(root = state.activeRoot, options = {}) {
    if (!(root instanceof HTMLElement)) return;

    root.classList.remove('is-open');
    setLock(false);

    state.closeTimer = window.setTimeout(() => {
      if (!root.classList.contains('is-open')) {
        root.hidden = true;
      }
    }, 190);

    const shouldRestoreFocus = options.restoreFocus !== false;
    if (shouldRestoreFocus && state.activeTrigger instanceof HTMLElement && state.activeTrigger.isConnected) {
      state.activeTrigger.focus({ preventScroll: true });
    }

    if (state.activeRoot === root) {
      state.activeRoot = null;
      state.activeTrigger = null;
    }
  }

  function findRootForCheckout(checkoutButton) {
    const ctas = checkoutButton.closest('.cart__ctas');
    if (!(ctas instanceof HTMLElement)) return null;

    const root = ctas.querySelector('[data-corgi-shipping-saver]');
    return root instanceof HTMLElement ? root : null;
  }

  function getCheckoutButton(root) {
    const ctas = root.closest('.cart__ctas');
    if (!(ctas instanceof HTMLElement)) return null;

    const button = ctas.querySelector('.cart__checkout-button[name="checkout"]');
    return button instanceof HTMLButtonElement ? button : null;
  }

  function continueToCheckout(root) {
    if (!(root instanceof HTMLElement)) return;

    const checkoutButton = getCheckoutButton(root);
    const formId = checkoutButton?.getAttribute('form') || 'cart-form';
    const form = document.getElementById(formId) || checkoutButton?.form;

    state.bypassCheckout = true;
    closeRoot(root, { restoreFocus: false });

    window.setTimeout(() => {
      state.bypassCheckout = false;
    }, 1500);

    window.requestAnimationFrame(() => {
      if (form instanceof HTMLFormElement && typeof form.requestSubmit === 'function' && checkoutButton) {
        form.requestSubmit(checkoutButton);
        return;
      }

      if (checkoutButton instanceof HTMLButtonElement) {
        checkoutButton.click();
        return;
      }

      if (form instanceof HTMLFormElement) {
        form.submit();
      }
    });
  }

  function getFocusable(root) {
    return Array.from(
      root.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => element instanceof HTMLElement && element.offsetParent !== null);
  }

  function reopenAfterUpdate() {
    if (!state.reopenAfterCartUpdate) return;

    state.reopenAfterCartUpdate = false;

    window.requestAnimationFrame(() => {
      const roots = Array.from(document.querySelectorAll('[data-corgi-shipping-saver]')).filter(
        (root) => root instanceof HTMLElement
      );
      const root = roots.find((candidate) => candidate.dataset.reached === 'true') || roots.find((candidate) => getItems(candidate).length > 0);

      if (root instanceof HTMLElement) {
        openRoot(root, state.activeTrigger);
      }
    });
  }

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const continueButton = target.closest('[data-corgi-shipping-saver-continue]');
    if (continueButton instanceof HTMLElement) {
      event.preventDefault();
      const root = continueButton.closest('[data-corgi-shipping-saver]');
      if (root instanceof HTMLElement) continueToCheckout(root);
      return;
    }

    const closeButton = target.closest('[data-corgi-shipping-saver-close]');
    if (closeButton instanceof HTMLElement) {
      event.preventDefault();
      const root = closeButton.closest('[data-corgi-shipping-saver]');
      closeRoot(root instanceof HTMLElement ? root : state.activeRoot);
      return;
    }

    const addButton = target.closest('[data-corgi-shipping-saver-add]');
    if (addButton instanceof HTMLElement) {
      state.reopenAfterCartUpdate = true;
      const root = addButton.closest('[data-corgi-shipping-saver]');
      if (root instanceof HTMLElement) {
        state.activeRoot = root;
      }
      return;
    }

    const checkoutButton = target.closest('.cart__checkout-button[name="checkout"]');
    if (!(checkoutButton instanceof HTMLButtonElement) || checkoutButton.disabled) return;

    if (state.bypassCheckout) {
      state.bypassCheckout = false;
      return;
    }

    const root = findRootForCheckout(checkoutButton);
    if (!shouldBlockCheckout(root)) return;

    event.preventDefault();
    openRoot(root, checkoutButton);
  });

  document.addEventListener('keydown', (event) => {
    if (!(state.activeRoot instanceof HTMLElement)) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeRoot(state.activeRoot);
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = getFocusable(state.activeRoot);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener('corgi:cart-surface:refreshed', () => {
    document.querySelectorAll('[data-corgi-shipping-saver]').forEach((root) => {
      if (root instanceof HTMLElement) orderItems(root);
    });
    reopenAfterUpdate();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      document.querySelectorAll('[data-corgi-shipping-saver]').forEach((root) => {
        if (root instanceof HTMLElement) orderItems(root);
      });
    });
  } else {
    document.querySelectorAll('[data-corgi-shipping-saver]').forEach((root) => {
      if (root instanceof HTMLElement) orderItems(root);
    });
  }
})();
